import { Component, DestroyRef, inject, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';

import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule, ValidatorFn, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { GalaxyAlertModule } from '@vendasta/galaxy/alert';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { SMSI18NModule } from '../../../assets/i18n/sms-i18n.module';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import type {
  A2PRegistrationInfo,
  A2PRegistrationStatus,
  AuthorizedRepresentative,
  Business,
  CampaignInfo,
  RegistrationIdentifier,
  UpsertA2PRegistrationInfoRequest,
} from '@vendasta/protos/sms/v2/types';
import type { DataField } from '@vendasta/protos/sms/v2/enums';
import { Subscription, combineLatest, take } from 'rxjs';
import { A2PRegistrationService } from '../a2p-registration.service';
import {
  DATA_FIELD_TO_FORM_FIELD,
  EIN_REGEX,
  IDENTIFIER_TYPE_EIN,
  RegistrationStage,
  URL_REGEX,
  ZIP_CODE_REGEX,
} from '../a2p-registration.constants';
import { createGroupValueChangeValidator } from '../a2p-helper-functions';
import { ConfirmationModalComponent } from './comfirmation-dialog/confirmation-modal.component';
import { BusinessCategoryToLabelPipe } from './pipes/business-category-to-label.pipe';
import { RegionToLabelPipe } from './pipes/region-to-label.pipe';
import { BrandFormControls, BusinessCategory, BusinessType, CampaignFormControls, Position, Region } from './types';

const PHONE_NUMBER_REGEX = /^\+\d{1,3}-\d{3}-\d{3}-\d{4}$/;

const additionalAddressLine = 'additionalAddressLine';

@Component({
  selector: 'sms-registration-form',
  imports: [
    ReactiveFormsModule,
    MatCardModule,
    MatIconModule,
    MatSelectModule,
    MatInputModule,
    MatButtonModule,
    GalaxyAlertModule,
    GalaxyFormFieldModule,
    TranslateModule,
    SMSI18NModule,
    BusinessCategoryToLabelPipe,
    RegionToLabelPipe,
  ],
  templateUrl: './sms-registration-form.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./sms-registration-form.component.scss'],
})
export class SmsRegistrationFormComponent implements OnInit, OnDestroy {
  BusinessCategories = Object.values(BusinessCategory);
  BusinessTypes = Object.values(BusinessType);
  Regions = Object.values(Region);
  JobPositions = Object.values(Position);

  private smsRegistrationService = inject(A2PRegistrationService);
  private snackbarService = inject(SnackbarService);
  private modal = inject(MatDialog);
  private destroyRef = inject(DestroyRef);
  private translateService = inject(TranslateService);

  smsForm = new FormGroup({
    brand: new FormGroup<BrandFormControls>({
      businessName: new FormControl('', { nonNullable: true, validators: [Validators.maxLength(128)] }),
      businessCategory: new FormControl('', { nonNullable: true }),
      businessType: new FormControl('', { nonNullable: true }),
      regionOfOperation: new FormControl('', { nonNullable: true }),
      businessRegistrationNumber: new FormControl('', {
        nonNullable: true,
        validators: [Validators.pattern(EIN_REGEX)],
      }),
      address: new FormControl('', { nonNullable: true }),
      additionalAddressLine: new FormControl('', { nonNullable: true }),
      state: new FormControl('', { nonNullable: true }),
      city: new FormControl('', { nonNullable: true }),
      zipCode: new FormControl('', { nonNullable: true, validators: [Validators.pattern(ZIP_CODE_REGEX)] }),
      website: new FormControl('', { nonNullable: true }),
      firstName: new FormControl('', { nonNullable: true }),
      lastName: new FormControl('', { nonNullable: true }),
      jobPosition: new FormControl('', { nonNullable: true }),
      businessTitle: new FormControl('', { nonNullable: true }),
      email: new FormControl('', { nonNullable: true, validators: [Validators.email] }),
      phoneNumber: new FormControl('', { nonNullable: true, validators: [Validators.pattern(PHONE_NUMBER_REGEX)] }),
    }),
    campaign: new FormGroup<CampaignFormControls>({
      privacyPolicyUrl: new FormControl('', { nonNullable: true, validators: [Validators.pattern(URL_REGEX)] }),
      termsAndConditionsUrl: new FormControl('', { nonNullable: true, validators: [Validators.pattern(URL_REGEX)] }),
    }),
  });

  formLocked = false;
  isCampaignErrorState = false;
  registrationStage: RegistrationStage = RegistrationStage.NotSubmitted;
  initialRegistrationFormInfo: Map<string, string> = new Map<string, string>();
  fieldsWithIssues: string[] = [];
  customValidatorsMap = new Map<string, ValidatorFn>();

  subscriptions: Subscription[] = [];

  get brandForm(): FormGroup<BrandFormControls> {
    return this.smsForm.controls.brand;
  }

  get campaignForm(): FormGroup<CampaignFormControls> {
    return this.smsForm.controls.campaign;
  }

  private get allControls(): Record<string, FormControl<string>> {
    return { ...this.brandForm.controls, ...this.campaignForm.controls };
  }

  ngOnInit(): void {
    this.getRegistration();
    const sub = this.brandForm.controls.jobPosition.valueChanges.subscribe((value: string) => {
      const businessTitle = this.brandForm.controls.businessTitle.value;
      const businessTitleInJobPositions = this.JobPositions.some((p) => p === businessTitle);

      if (value !== Position.otherPosition && (businessTitle === '' || businessTitleInJobPositions)) {
        this.brandForm.controls.businessTitle.patchValue(value);
      } else if (value === Position.otherPosition && businessTitleInJobPositions) {
        this.brandForm.controls.businessTitle.patchValue('');
      }
    });
    this.subscriptions.push(sub);

    const controls = this.allControls;
    Object.keys(controls).forEach((key) => {
      this.subscriptions.push(
        controls[key].valueChanges.subscribe(() => {
          if (this.fieldsWithIssues.includes(key)) {
            this.fieldsWithIssues.forEach((fieldName) => {
              this.allControls[fieldName]?.updateValueAndValidity({ emitEvent: false });
            });
          }
        }),
      );
    });
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((s) => s.unsubscribe());
  }

  submitRegistrationForm(): void {
    this.toggleSubmitValidators(true);
    if (this.smsForm.invalid) {
      this.snackbarService.openErrorSnack(
        this.translateService.instant('SMS_REGISTRATION.FORM.SNACKBAR.INVALID_SUBMISSION'),
      );
      return;
    }

    this.modal
      .open(ConfirmationModalComponent, { width: '420px' })
      .afterClosed()
      .pipe(take(1))
      .subscribe((result) => {
        if (result) {
          this.upsertData(true);
        }
      });
  }

  saveChanges(): void {
    // Don't show errors for empty required fields & unchanged error fields, when doing partial save
    this.toggleSubmitValidators(false);

    // Check for formatting errors
    const controls = this.allControls;
    const hasFormatErrors = Object.keys(controls).some((key) => {
      const errors = controls[key].errors;
      if (errors === null) {
        return;
      }

      return Object.keys(errors).some((errorKey) => errorKey !== 'required');
    });

    // If there are formatting errors don't save the form
    if (hasFormatErrors) {
      this.snackbarService.openErrorSnack(
        this.translateService.instant('SMS_REGISTRATION.FORM.SNACKBAR.INVALID_FORMAT'),
      );

      return;
    }

    this.upsertData(false);
  }

  upsertData(isSubmitted: boolean): void {
    const form = this.smsForm.getRawValue();
    //  TODO: get country from form
    const country = 'US';

    const request = {
      business: {
        legalName: form.brand.businessName,
        businessType: form.brand.businessType,
        industry: form.brand.businessCategory,
        registrationIdentifier: {
          identifierType: IDENTIFIER_TYPE_EIN,
          identifier: form.brand.businessRegistrationNumber,
        } as RegistrationIdentifier,
        regionOfOperation: form.brand.regionOfOperation,
        websiteUrl: form.brand.website,
        socialMediaProfileUrls: [],
        streetAddress: form.brand.address,
        streetAddressSecondary: form.brand.additionalAddressLine,
        city: form.brand.city,
        state: form.brand.state,
        postalCode: form.brand.zipCode,
        country: country,
      } as Business,
      campaignInfo: {
        privacyPolicyUrl: form.campaign.privacyPolicyUrl,
        termsAndConditionsUrl: form.campaign.termsAndConditionsUrl,
      } as CampaignInfo,
      authorizedRepresentative: {
        firstName: form.brand.firstName,
        lastName: form.brand.lastName,
        email: form.brand.email,
        phoneNumber: form.brand.phoneNumber,
        businessTitle: form.brand.businessTitle,
        jobPosition: form.brand.jobPosition,
      } as AuthorizedRepresentative,
      submitted: isSubmitted,
    } as UpsertA2PRegistrationInfoRequest;
    this.smsRegistrationService
      .upsertA2pRegistration(request)
      .then(() => {
        this.snackbarService.openWithOptions(
          this.translateService.instant('SMS_REGISTRATION.FORM.SNACKBAR.SAVE_SUCCESS'),
          {
            panelClass: ['success-snackbar'],
          },
        );
        if (isSubmitted) {
          this.lockForm();
        }
      })
      .catch(() => {
        this.snackbarService.openErrorSnack(this.translateService.instant('SMS_REGISTRATION.FORM.SNACKBAR.SAVE_ERROR'));
      });
  }

  getRegistration(): void {
    combineLatest([
      this.smsRegistrationService.a2PRegistrationInfo$,
      this.smsRegistrationService.registrationStage$,
      this.smsRegistrationService.a2pRegistrationStatus$,
      this.smsRegistrationService.getPrefillA2PFormInfo(),
    ])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(
        ([a2pRegistrationInfo, registrationStage, registrationStatus, a2PFormInfo]) => {
          if (!a2pRegistrationInfo) return;
          this.registrationStage = registrationStage;
          this.prefillFormWithA2PFormInfo(a2PFormInfo as A2PRegistrationInfo);

          if (registrationStage === RegistrationStage.CampaignError) {
            this.isCampaignErrorState = true;
            this.lockBrandFieldsOnly();
          } else if (
            registrationStage !== RegistrationStage.NotSubmitted &&
            registrationStage !== RegistrationStage.DataError
          ) {
            this.isCampaignErrorState = false;
            this.lockForm();
          } else {
            this.isCampaignErrorState = false;
            this.unlockForm();
          }

          if (registrationStage === RegistrationStage.DataError) {
            this.initialRegistrationFormInfo = this.getInitialRegistrationFormInfo(a2pRegistrationInfo);
            this.fieldsWithIssues = this.getFormFieldsWithIssues(registrationStatus);
            const controls = this.allControls;
            this.fieldsWithIssues.forEach((fieldName) => {
              const control = controls[fieldName];
              if (control) {
                control.markAsTouched();
              }
            });
            this.toggleSubmitValidators(true);
          }
        },
        (error) => {
          if (error.status !== 404) {
            this.snackbarService.openErrorSnack(
              this.translateService.instant('SMS_REGISTRATION.FORM.SNACKBAR.FETCH_ERROR'),
            );
          }
        },
      );
  }

  lockForm(): void {
    this.formLocked = true;
    this.smsForm.disable();
  }

  unlockForm(): void {
    this.formLocked = false;
    this.smsForm.enable();
  }

  lockBrandFieldsOnly(): void {
    this.formLocked = false;
    this.smsForm.enable();
    this.brandForm.disable();
  }

  toggleSubmitValidators(on: boolean): void {
    const optionalFields = [additionalAddressLine];
    const controls = this.allControls;
    Object.keys(controls).forEach((key) => {
      const control = controls[key];
      if (on) {
        if (!optionalFields.includes(key)) {
          control.addValidators(Validators.required);
        }
        if (this.fieldsWithIssues.includes(key) && this.registrationStage === RegistrationStage.DataError) {
          const val = this.initialRegistrationFormInfo.get(key);
          if (val !== undefined) {
            const smartValidatorFn = createGroupValueChangeValidator(
              this.fieldsWithIssues,
              this.initialRegistrationFormInfo,
              () => this.allControls,
            );
            control.addValidators(smartValidatorFn);
            this.customValidatorsMap.set(key, smartValidatorFn);
          }
        }
      } else {
        control.removeValidators(Validators.required);
        const customValidatorFn = this.customValidatorsMap.get(key);
        if (customValidatorFn) {
          control.removeValidators(customValidatorFn);
          this.customValidatorsMap.delete(key);
        }
      }

      control.updateValueAndValidity();
    });
  }

  getInitialRegistrationFormInfo(a2PRegistrationInfo: A2PRegistrationInfo): Map<string, string> {
    const result = new Map<string, string>();
    result.set('businessName', a2PRegistrationInfo?.business?.legalName || '');
    result.set('businessCategory', a2PRegistrationInfo?.business?.industry || '');
    result.set('businessType', a2PRegistrationInfo?.business?.businessType || '');
    result.set('regionOfOperation', a2PRegistrationInfo?.business?.regionOfOperation || '');
    result.set('businessRegistrationNumber', a2PRegistrationInfo?.business?.registrationIdentifier?.identifier || '');
    result.set('address', a2PRegistrationInfo?.business?.streetAddress || '');
    result.set('additionalAddressLine', a2PRegistrationInfo?.business?.streetAddressSecondary || '');
    result.set('state', a2PRegistrationInfo?.business?.state || '');
    result.set('city', a2PRegistrationInfo?.business?.city || '');
    result.set('zipCode', a2PRegistrationInfo?.business?.postalCode || '');
    result.set('website', a2PRegistrationInfo?.business?.websiteUrl || '');
    result.set('firstName', a2PRegistrationInfo?.authorizedRepresentative?.firstName || '');
    result.set('lastName', a2PRegistrationInfo?.authorizedRepresentative?.lastName || '');
    result.set('jobPosition', a2PRegistrationInfo?.authorizedRepresentative?.jobPosition || '');
    result.set('businessTitle', a2PRegistrationInfo?.authorizedRepresentative?.businessTitle || '');
    result.set('email', a2PRegistrationInfo?.authorizedRepresentative?.email || '');
    result.set('phoneNumber', a2PRegistrationInfo?.authorizedRepresentative?.phoneNumber || '');
    return result;
  }

  getFormFieldsWithIssues(registrationStatus: A2PRegistrationStatus): string[] {
    const result: string[] = [];
    if (registrationStatus === null || registrationStatus === undefined) {
      return [];
    }
    if (
      !!registrationStatus.secondaryBusinessProfile &&
      registrationStatus.secondaryBusinessProfile.status === 'VALIDATION_STATUS_NON_COMPLIANT' &&
      registrationStatus.secondaryBusinessProfile.fieldsWithIssues &&
      registrationStatus.secondaryBusinessProfile.fieldsWithIssues.length > 0
    ) {
      registrationStatus.secondaryBusinessProfile.fieldsWithIssues.forEach((field) => {
        const val = DATA_FIELD_TO_FORM_FIELD.get(field);
        if (val !== undefined) {
          result.push(val);
        }
      });
    }
    if (
      !!registrationStatus.trustBundle &&
      registrationStatus.trustBundle.status === 'VALIDATION_STATUS_NON_COMPLIANT' &&
      !!registrationStatus.trustBundle.fieldsWithIssues &&
      registrationStatus.trustBundle.fieldsWithIssues.length > 0
    ) {
      registrationStatus.trustBundle.fieldsWithIssues.forEach((field) => {
        const val = DATA_FIELD_TO_FORM_FIELD.get(field);
        if (val !== undefined) {
          result.push(val);
        }
      });
    }
    if (!!registrationStatus.brand && registrationStatus.brand.status === 'BRAND_STATUS_FAILED') {
      if (registrationStatus.brand.fieldsWithIssues && registrationStatus.brand.fieldsWithIssues.length > 0) {
        registrationStatus.brand.fieldsWithIssues.forEach((field: DataField) => {
          const val = DATA_FIELD_TO_FORM_FIELD.get(field);
          if (val !== undefined) {
            result.push(val);
          }
        });
      }
    }

    return result;
  }

  prefillFormWithA2PFormInfo(a2pRegistrationInfo: A2PRegistrationInfo): void {
    this.brandForm.patchValue({
      businessName: a2pRegistrationInfo?.business?.legalName,
      businessCategory: a2pRegistrationInfo?.business?.industry,
      businessType: a2pRegistrationInfo?.business?.businessType,
      regionOfOperation: a2pRegistrationInfo?.business?.regionOfOperation,
      businessRegistrationNumber: a2pRegistrationInfo?.business?.registrationIdentifier?.identifier,
      address: a2pRegistrationInfo?.business?.streetAddress,
      additionalAddressLine: a2pRegistrationInfo?.business?.streetAddressSecondary,
      state: a2pRegistrationInfo?.business?.state,
      city: a2pRegistrationInfo?.business?.city,
      zipCode: a2pRegistrationInfo?.business?.postalCode,
      website: a2pRegistrationInfo?.business?.websiteUrl,
      firstName: a2pRegistrationInfo?.authorizedRepresentative?.firstName,
      lastName: a2pRegistrationInfo?.authorizedRepresentative?.lastName,
      jobPosition: a2pRegistrationInfo?.authorizedRepresentative?.jobPosition,
      businessTitle: a2pRegistrationInfo?.authorizedRepresentative?.businessTitle,
      email: a2pRegistrationInfo?.authorizedRepresentative?.email,
      phoneNumber: a2pRegistrationInfo?.authorizedRepresentative?.phoneNumber,
    });
    this.campaignForm.patchValue({
      privacyPolicyUrl: a2pRegistrationInfo?.campaignInfo?.privacyPolicyUrl,
      termsAndConditionsUrl: a2pRegistrationInfo?.campaignInfo?.termsAndConditionsUrl,
    });
  }
}
